import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { ChangeEvent, Dispatch, useCallback, useEffect, useMemo } from 'react';

import { RootActions } from '../../CorePackage/MainModule/types/RootActions';
import {
  passportGroupPassportGetNextPageAction,
  passportGroupPassportGetPageAction,
} from '../MainModule/actions/passportGroupActions';
import styles from './PassportGroupPassportsTable.module.scss';
import {
  passportGroupPassportListSelector,
  passportGroupPassportListTotalCount,
} from '../MainModule/selectors/passportGroupSelector';
import { IPassportGroupPassport } from '../../APIPackage/PassportGroupsModule/interfaces/IPassportGroupPassport';
import { PassportGroupPassportTypeEnum } from '../PassportGroupFormModule/enums/PassportGroupPassportTypeEnum';
import { ROUTE_DECISION, ROUTE_KNM, ROUTE_PM } from '../../CorePackage/MainModule/routes/routes';
import { Button } from '../../UIPackage/MainModule/components/Button';
import { Checkbox } from '../../UIPackage/MainModule/components/Checkbox';
import { knmErpMapStatusNameByNewName } from '../../KnmErpPackage/MainModule/constants/knmErpMapStatusNameByNewName';

const getPassportUrl = (passport: IPassportGroupPassport) => {
  switch (passport.type) {
    case PassportGroupPassportTypeEnum.ERKNM:
    case PassportGroupPassportTypeEnum.ERP:
      return ROUTE_KNM(passport.id);
    case PassportGroupPassportTypeEnum.PM:
      return ROUTE_PM(passport.id);
    case PassportGroupPassportTypeEnum.DECISION_PASSPORT:
      return ROUTE_DECISION(passport.id);
    default:
      return '';
  }
};

const getPassportDates = (passport: IPassportGroupPassport) => {
  if (passport.startDate && passport.stopDate) {
    return `${passport.startDate} - ${passport.stopDate}`;
  }

  return passport.startDate || '';
};

interface IPassportGroupPassportsTableProps {
  isShowCheckbox: boolean;
  checkedItems: { [p: number]: boolean };
  setCheckedItems: (value: { [p: number]: boolean }) => void;
}

export const PassportGroupPassportsTable = ({
  isShowCheckbox,
  checkedItems,
  setCheckedItems,
}: IPassportGroupPassportsTableProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const { id } = useParams<{ id: string }>();
  const passports = useSelector(passportGroupPassportListSelector);
  const totalCount = useSelector(passportGroupPassportListTotalCount);

  const isCheckedAllItems = useMemo(() => passports.every(item => checkedItems[item.id]), [passports, checkedItems]);

  useEffect(() => {
    dispatch(passportGroupPassportGetPageAction(id));
  }, [dispatch]);

  const handleNextPage = useCallback(() => {
    dispatch(passportGroupPassportGetNextPageAction(id));
  }, [id, dispatch]);

  const handleSelectAllPassports = useCallback(() => {
    if (isCheckedAllItems) {
      setCheckedItems({});
    } else {
      const newCheckedItems: { [p: number]: boolean } = {};

      passports.forEach(item => {
        newCheckedItems[item.id] = true;
      });
      setCheckedItems(newCheckedItems);
    }
  }, [setCheckedItems, passports, isCheckedAllItems]);

  const handleSelectPassport = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const idNumber = Number.parseInt(e.target.id, 10);

      setCheckedItems({ ...checkedItems, [idNumber]: e.target.checked });
    },
    [checkedItems, setCheckedItems],
  );

  return (
    <div className={styles.Content}>
      <div>
        <table className={styles.Table}>
          <thead>
            <tr className={styles.TableHeader}>
              {isShowCheckbox && (
                <th className={styles.CheckboxColumn}>
                  <Checkbox theme="system" checked={isCheckedAllItems} onChange={handleSelectAllPassports} />
                </th>
              )}
              <th>Номер</th>
              <th>Контрольный (надзорный) орган</th>
              <th>Орган прокуратуры</th>
              <th>Категория</th>
              <th>Статус</th>
              <th>Период проведения</th>
            </tr>
          </thead>
          <tbody>
            {passports.map(item => (
              <tr key={item.id} className={styles.TableRow}>
                {isShowCheckbox && (
                  <td className={styles.CheckboxColumn}>
                    <Checkbox
                      theme="system"
                      id={item.id.toString()}
                      onChange={handleSelectPassport}
                      checked={checkedItems[item.id] || false}
                    />
                  </td>
                )}
                <td>
                  <a
                    className="shared-table-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={getPassportUrl(item)}
                  >
                    {item.number}
                  </a>
                </td>
                <td>{item.controllingOrganization}</td>
                <td>{item.prosecutorOrganization}</td>
                <td>{item.typeName}</td>
                <td>
                  {item.type === PassportGroupPassportTypeEnum.ERP
                    ? knmErpMapStatusNameByNewName[item.status || '']
                    : item.status}
                </td>
                <td>{getPassportDates(item)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {totalCount > passports.length && <Button text="Показать ещё" color="load" onClick={handleNextPage} />}
      </div>
    </div>
  );
};
